import { useLocation, useHistory } from 'react-router-dom'
import { useMemo, useEffect } from 'react'

type SetSearchParams<T> = (params: Partial<T>, options?: { replace?: boolean }) => void

// Utility function to perform a deep comparison of two values
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((val, i) => deepEqual(val, b[i]))
  if (typeof a === 'object' && a !== null && b !== null) {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    return aKeys.length === bKeys.length && aKeys.every((key) => deepEqual(a[key], b[key]))
  }
  return false
}

const useSearchParams = <T extends Record<string, any>>(
  initialValues?: T,
  defaultValues?: Partial<T>
) => {
  const location = useLocation()
  const history = useHistory()

  const searchParams = useMemo(() => {
    const params: Partial<T> = initialValues ? { ...initialValues } : {}
    const searchParams = new URLSearchParams(location.search)

    searchParams.forEach((value, key) => {
      try {
        params[key as keyof T] = JSON.parse(value)
      } catch {
        params[key as keyof T] = value
      }
    })

    // Use defaultValues if a key is not present in the URL search params
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, defaultValue]) => {
        if (!(key in params)) {
          params[key as keyof T] = defaultValue
        }
      })
    }

    return params as T
  }, [location.search, JSON.stringify(initialValues), JSON.stringify(defaultValues)])

  const setSearchParams: SetSearchParams<T> = (params, options = {}) => {
    const newSearchParams = new URLSearchParams(location.search)

    Object.entries(params).forEach(([key, value]) => {
      const defaultValue = defaultValues?.[key]

      if (value === null || value === undefined || (defaultValue !== undefined && deepEqual(value, defaultValue))) {
        newSearchParams.delete(key)
      } else {
        newSearchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
      }
    })

    if (initialValues && newSearchParams.toString() === '') {
      Object.entries(initialValues).forEach(([key, value]) => {
        const defaultValue = defaultValues?.[key]
        if (!deepEqual(value, defaultValue)) {
          newSearchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
        }
      })
    }

    const newLocation = {
      pathname: location.pathname,
      search: newSearchParams.toString(),
    }

    if (options.replace) {
      history.replace(newLocation)
    } else {
      history.push(newLocation)
    }
  }

  useEffect(() => {
    if (initialValues && !location.search) {
      setSearchParams(initialValues, { replace: true })
    }
  }, [location.search, JSON.stringify(initialValues)])

  return [searchParams, setSearchParams] as [T, SetSearchParams<T>]
}

export default useSearchParams
